<script lang="ts" setup>
import { ref, reactive, inject, watch, onMounted, onBeforeUnmount } from 'vue';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { PerspectiveEvents } from '@/viewerExtensions/events';
import { waitFor } from '@immersive-tech/viewer-engine';
import CollapseFieldset from '@/components/CollapseFieldset.vue';
import ToggleBox from '@/components/ToggleBox.vue';
import ZoomRestriction from '@/components/Perspectives/ZoomRestriction.vue';
import AngleRestriction from '@/components/Perspectives/AngleRestriction.vue';
import CameraCoordinate from '@/components/Perspectives/CameraCoordinate.vue';
import MobileOffset from '@/components/Perspectives/MobileOffset.vue';

import type { Ref } from 'vue';
import type { Viewer, Extension, Nullable } from '@immersive-tech/viewer-engine';
import type { IManifest } from '@/types/IManifest';
import type { Perspectives } from '@/viewerExtensions';
import type { IExtensionCameraPositionChangeEvent } from '@/types/events/IExtensionCameraPositionChangeEvent';
import type { IExtensionCameraTargetChangeEvent } from '@/types/events/IExtensionCameraTargetChangeEvent';

const viewer = inject('viewer') as Ref<Nullable<Viewer>>;
const manifest = inject('manifest') as Ref<IManifest>;
const cameraPosition = reactive(Vector3.Zero());
const cameraTarget = reactive(Vector3.Zero());
const eventListenersAdded = ref(false);
const activePerspective = inject('activePerspective') as Ref<Nullable<string>>;
const perspective = ref({ ...manifest.value.views[activePerspective.value || 'Main'] });
const perspectiveName = ref(activePerspective.value || 'Main');
const perspectivesExtension = viewer.value?.extensions.get('Perspectives') as Extension<Perspectives>;
const perspectiveExistsErrorShow = ref(false);
const previewRestrictions = inject('previewRestrictions') as Ref<boolean>;
const skipCameraWatchers = ref(false);

const cameraPositionWatcher = (event: CustomEvent<IExtensionCameraPositionChangeEvent>) => {
  if (previewRestrictions.value === true) return;
  if (skipCameraWatchers.value === true) return;

  cameraPosition.set(event.detail.cameraPosition.x, event.detail.cameraPosition.y, event.detail.cameraPosition.z);
};

const cameraTargetWatcher = (event: CustomEvent<IExtensionCameraTargetChangeEvent>) => {
  if (previewRestrictions.value === true) return;
  if (skipCameraWatchers.value === true) return;

  cameraTarget.set(event.detail.cameraTarget.x, event.detail.cameraTarget.y, event.detail.cameraTarget.z);
};

const resetViewHandler = () => {
  previewRestrictions.value = false;
};

const addEventListeners = () => {
  if (eventListenersAdded.value === true) return;

  viewer.value?.addEventListener(PerspectiveEvents.CAMERA_POSITION_CHANGE, cameraPositionWatcher as EventListener);
  viewer.value?.addEventListener(PerspectiveEvents.CAMERA_TARGET_CHANGE, cameraTargetWatcher as EventListener);
  viewer.value?.addEventListener(PerspectiveEvents.RESET_VIEW, resetViewHandler);
};

const setInitialCameraPosition = async () => {
  if (!viewer.value) return;
  if (!viewer.value.camera) return;

  cameraTarget.set(perspective.value.target.x, perspective.value.target.y, perspective.value.target.z);
  cameraPosition.set(perspective.value.position.x, perspective.value.position.y, perspective.value.position.z);

  if (activePerspective.value) {
    await perspectivesExtension.setPerspective(activePerspective.value);
  }
};

const mountHandler = async () => {
  await setInitialCameraPosition();
  addEventListeners();
};

const unmountHandler = () => {
  previewRestrictions.value = false;

  viewer.value?.removeEventListener(PerspectiveEvents.CAMERA_POSITION_CHANGE, cameraPositionWatcher as EventListener);
  viewer.value?.removeEventListener(PerspectiveEvents.CAMERA_TARGET_CHANGE, cameraTargetWatcher as EventListener);
  viewer.value?.removeEventListener(PerspectiveEvents.RESET_VIEW, resetViewHandler);
};

const activePerspectiveWatcher = (newValue: Nullable<string>) => {
  perspective.value = { ...manifest.value.views[newValue || 'Main'] };
  perspectiveName.value = newValue || 'Main';

  if (activePerspective.value) {
    perspectivesExtension.setPerspective(activePerspective.value);
  }
};

const updateButtonClick = () => {
  if (perspectiveName.value === '') return;

  if ((perspectiveName.value in manifest.value.views) && activePerspective.value !== perspectiveName.value) {
    perspectiveExistsErrorShow.value = true;
    return;
  }

  manifest.value.views[perspectiveName.value] = {
    target: { x: cameraTarget.x, y: cameraTarget.y, z: cameraTarget.z },
    position: { x: cameraPosition.x, y: cameraPosition.y, z: cameraPosition.z },
    angle: perspective.value.angle,
    zoomRestriction: perspective.value.zoomRestriction,
    mobileOffset: perspective.value.mobileOffset,
  }

  if(activePerspective.value !== perspectiveName.value) {
    for (const hotspot in manifest.value.hotspots) {
      if(manifest.value.hotspots[hotspot].perspective === activePerspective.value) {
        manifest.value.hotspots[hotspot].perspective = perspectiveName.value;
      }
    }

    if (activePerspective.value) {
      delete manifest.value.views[activePerspective.value];
    }
  }

  activePerspective.value = null;
};

const deleteButtonClick = () => {
  if (!activePerspective.value || activePerspective.value === '') {
    return;
  }

  delete manifest.value.views[activePerspective.value];
  activePerspective.value = null;
};

const perspectiveExistsErrorShowWatcher = async (newValue: boolean) => {
  if (newValue === false) return;

  await waitFor(1500);

  perspectiveExistsErrorShow.value = false;
};

const viewerWatcher = (newValue: Nullable<Viewer>) => {
  if (!newValue) return;

  addEventListeners();
};

const previewRestrictionsWatcher = (newValue: boolean) => {
  if (newValue === true) {
    skipCameraWatchers.value = true;
  } else {
    viewer.value?.addEventListener(PerspectiveEvents.ZOOM_RESTRICTION_SET, async () => {
      await viewer.value?.interpolateCamera(cameraPosition, cameraTarget, 0);

      skipCameraWatchers.value = false;
    }, { once: true });
  }
};

const updateAngleRestriction = (newValue: Nullable<number>) => {
  perspective.value.angle = newValue;
};

const updateZoomRestriction = (newValue: [number, number]) => {
  perspective.value.zoomRestriction = newValue;
};

const updateCameraTarget = (newCoordinate: Vector3) => {
  if (!viewer.value) return;

  if (viewer.value.camera) {
    viewer.value.camera.target = newCoordinate;
  }

  viewer.value.renderNextFrame += 1;
};

const updateCameraPosition = (newCoordinate: Vector3) => {
  if (!viewer.value) return;

  if (viewer.value.camera) {
    viewer.value.camera.position = newCoordinate;
  }

  viewer.value.renderNextFrame += 1;
};

const updateMobileOffset = (newValue: Nullable<number>) => {
  perspective.value.mobileOffset = newValue;
};

watch(viewer, viewerWatcher);
watch(perspectiveExistsErrorShow, perspectiveExistsErrorShowWatcher);
watch(activePerspective, activePerspectiveWatcher);
watch(previewRestrictions, previewRestrictionsWatcher);
onMounted(mountHandler);
onBeforeUnmount(unmountHandler);
</script>

<template>
  <CollapseFieldset v-if="perspective">
    <template #legend>
      Update Perspective
    </template>

    <div v-if="perspectiveExistsErrorShow === true" class="bg-red-100 p-2 mb-1">
      <p class="text-xs">
        Perspective "<span class="font-bold">{{ perspectiveName }}</span>" already exists, choose a different name
      </p>
    </div>

    <div class="flex flex-col mb-1">
      <label for="perspective-name" class="text-xs">Perspective Name</label>
      <input id="perspective-name" v-model="perspectiveName" class="border text-xs p-1" type="text" :disabled="activePerspective === 'Main'">
    </div>

    <div class="flex flex-col mb-1">
      <label for="perspective-angle" class="text-xs">Perspective Angle Restriction</label>
      <AngleRestriction :angle-restriction="perspective.angle ?? ''" @change="updateAngleRestriction" />
    </div>

    <div class="flex flex-col grow mb-1">
      <label class="text-xs">Perspective Zoom Restriction</label>

      <ZoomRestriction :zoom-restriction="perspective.zoomRestriction" @change="updateZoomRestriction" />
    </div>

    <div class="flex flex-col mb-1">
      <label for="perspective-offset" class="text-xs">Mobile Offset</label>
      <MobileOffset :mobile-offset="perspective.mobileOffset ?? 0.5" @change="updateMobileOffset" />
    </div>

    <div class="flex flex-col mb-1">
      <ToggleBox v-model="previewRestrictions" label="Preview Restrictions" />
    </div>

    <div class="flex flex-col mb-1">
      <label class="text-xs">Camera Position</label>
      <CameraCoordinate :coordinate="cameraPosition" :editable="true" @change="updateCameraPosition" />
    </div>

    <div class="flex flex-col mb-1">
      <label class="text-xs">Camera Target</label>
      <CameraCoordinate :coordinate="cameraTarget" :editable="true" @change="updateCameraTarget" />
    </div>

    <div class="flex flex-col px-1 my-1">
      <button type="button" class="text-xs p-1 bg-lime-600 text-white mb-1" @click="updateButtonClick">
        Save
      </button>
      <button v-if="activePerspective !== 'Main'" type="button" class="text-xs p-1 bg-red-700 text-white" @click="deleteButtonClick">
        Delete
      </button>
    </div>
  </CollapseFieldset>
</template>
